import React, { useState, useEffect } from 'react';
import { X, Download, CheckCircle, ChevronRight } from 'lucide-react';
import { exportLeads } from '../services/exportService';
import { showToast } from '@/components/ui/Toast';

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRows: string[];
    totalFilteredCount?: number;
    currentFilters?: any;
    agentCreatedOnly?: boolean;
    apiEndpoint?: string; // Custom API endpoint for export (default: '/leads')
}

// Default columns to export (backwards compatible)
const DEFAULT_SELECTED_COLUMNS = [
    'phone', 'name', 'email',
    'disposition_l1', 'disposition_l2', 'disposition_l3',
    'owner', 'customer_type', 'created_date', 'last_updated_date', 'remarks',
    'last_abc_date', 'last_call_status', 'last_call_duration'
];

const COLUMN_GROUPS = [
    {
        label: 'Basic Information',
        columns: [
            { id: 'name', title: 'Name', default: true },
            { id: 'phone', title: 'Phone', default: true },
            { id: 'email', title: 'Email', default: true },
        ]
    },
    {
        label: 'Classification',
        columns: [
            { id: 'disposition_l1', title: 'Disposition L1', default: true },
            { id: 'disposition_l2', title: 'Disposition L2', default: true },
            { id: 'disposition_l3', title: 'Disposition L3', default: true },
        ]
    },
    {
        label: 'Communication & Activity',
        columns: [
            { id: 'owner', title: 'Owner', default: true },
            { id: 'customer_type', title: 'Customer Type', default: true },
            { id: 'remarks', title: 'Remarks', default: true },
            { id: 'last_call_status', title: 'Last Call Status', default: true },
            { id: 'last_call_duration', title: 'Last Call Duration', default: true },
        ]
    },
    {
        label: 'Dates',
        columns: [
            { id: 'created_date', title: 'Created Date', default: true },
            { id: 'last_updated_date', title: 'Last Updated Date', default: true },
            { id: 'last_abc_date', title: 'Last ABC Date', default: true },
        ]
    },
];

const ExportModal: React.FC<ExportModalProps> = ({
    isOpen,
    onClose,
    selectedRows,
    totalFilteredCount = 0,
    currentFilters = {},
    agentCreatedOnly = false,
    apiEndpoint = '/leads'
}) => {
    const [step, setStep] = useState<'type' | 'columns'>('type');
    const [exportType, setExportType] = useState<'all' | 'filtered' | 'selected'>('filtered');
    const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_SELECTED_COLUMNS);
    const [isExporting, setIsExporting] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [exportComplete, setExportComplete] = useState(false);

    // Auto-select "Selected Leads" when leads are selected
    useEffect(() => {
        if (selectedRows.length > 0 && exportType !== 'selected') {
            setExportType('selected');
        }
    }, [selectedRows]);

    const handleToggleColumnGroup = (groupIndex: number) => {
        const group = COLUMN_GROUPS[groupIndex];
        const groupColumnIds = group.columns.map(c => c.id);
        const allInGroupSelected = groupColumnIds.every(id => selectedColumns.includes(id));

        if (allInGroupSelected) {
            setSelectedColumns(selectedColumns.filter(id => !groupColumnIds.includes(id)));
        } else {
            const newCols = new Set(selectedColumns);
            groupColumnIds.forEach(id => newCols.add(id));
            setSelectedColumns(Array.from(newCols));
        }
    };

    const handleToggleColumn = (columnId: string) => {
        setSelectedColumns(prev =>
            prev.includes(columnId)
                ? prev.filter(id => id !== columnId)
                : [...prev, columnId]
        );
    };

    const handleExport = async () => {
        console.log('handleExport called. selectedColumns:', selectedColumns);
        if (selectedColumns.length === 0) {
            showToast('Please select at least one column to export', 'error');
            return;
        }

        setIsExporting(true);

        try {
            let exportOptions;
            console.log('Building export options. exportType:', exportType);

            switch (exportType) {
                case 'all':
                    exportOptions = {
                        export_data: true,
                        selected_columns: selectedColumns,
                        export_all: true,
                        primary_filters: agentCreatedOnly ? { agent_created: true } : {},
                        custom_filters: {},
                        apiEndpoint
                    };
                    break;

                case 'filtered':
                    exportOptions = {
                        export_data: true,
                        selected_columns: selectedColumns,
                        export_all: true,
                        primary_filters: {
                            ...(currentFilters.primary_filters || {}),
                            ...(agentCreatedOnly ? { agent_created: true } : {})
                        },
                        custom_filters: currentFilters.custom_filters || {},
                        apiEndpoint
                    };
                    break;

                case 'selected':
                    if (selectedRows.length === 0) {
                        showToast('Please select at least one lead to export', 'error');
                        return;
                    }
                    exportOptions = {
                        export_data: true,
                        selected_columns: selectedColumns,
                        selected_lead_ids: selectedRows,
                        export_all: false,
                        apiEndpoint
                    };
                    break;
            }

            console.log('exportOptions before calling exportLeads:', exportOptions);
            const url = await exportLeads(exportOptions);

            setDownloadUrl(url);
            setExportComplete(true);
            showToast('✅ Export completed! Click the download button to get your file.', 'success');

        } catch (error) {
            console.error('Export failed:', error);
            showToast(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleClose = () => {
        setDownloadUrl(null);
        setExportComplete(false);
        setStep('type');
        setExportType('filtered');
        setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
        onClose();
    };

    const handleDownload = () => {
        if (downloadUrl) {
            window.open(downloadUrl, '_blank');
        }
    };

    const getExportCount = () => {
        switch (exportType) {
            case 'all':
                return 'All leads';
            case 'filtered':
                return `${totalFilteredCount} filtered leads`;
            case 'selected':
                return `${selectedRows.length} selected leads`;
            default:
                return '0 leads';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
                    <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Download className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Export Leads</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {step === 'type' ? 'Step 1: Choose export type' : 'Step 2: Select columns'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Show download button if export is complete */}
                    {exportComplete && downloadUrl && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-green-900">Export Complete!</div>
                                        <div className="text-xs text-green-700">Your file is ready to download</div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </button>
                            </div>
                        </div>
                    )}

                    {/* STEP 1: Export Type Selection */}
                    {!exportComplete && step === 'type' && (
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700">
                                What would you like to export?
                            </label>

                            {/* All Leads */}
                            <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                    type="radio"
                                    name="exportType"
                                    value="all"
                                    checked={exportType === 'all'}
                                    onChange={(e) => setExportType(e.target.value as any)}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <div className="ml-3 flex-1">
                                    <div className="text-sm font-medium text-gray-900">All Leads</div>
                                    <div className="text-xs text-gray-500">Export all leads in the database</div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                            </label>

                            {/* Filtered Leads */}
                            <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                    type="radio"
                                    name="exportType"
                                    value="filtered"
                                    checked={exportType === 'filtered'}
                                    onChange={(e) => setExportType(e.target.value as any)}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <div className="ml-3 flex-1">
                                    <div className="text-sm font-medium text-gray-900">Current Filter Results</div>
                                    <div className="text-xs text-gray-500">
                                        Export {totalFilteredCount} leads matching current filters
                                    </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                            </label>

                            {/* Selected Leads */}
                            <label className={`flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                                selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                            }`}>
                                <input
                                    type="radio"
                                    name="exportType"
                                    value="selected"
                                    checked={exportType === 'selected'}
                                    onChange={(e) => setExportType(e.target.value as any)}
                                    disabled={selectedRows.length === 0}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <div className="ml-3 flex-1">
                                    <div className="text-sm font-medium text-gray-900">Selected Leads</div>
                                    <div className="text-xs text-gray-500">
                                        Export {selectedRows.length} manually selected leads
                                        {selectedRows.length === 0 && ' (No leads selected)'}
                                    </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                            </label>
                        </div>
                    )}

                    {/* STEP 2: Column Selection */}
                    {!exportComplete && step === 'columns' && (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Select columns to export
                            </label>

                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {COLUMN_GROUPS.map((group, groupIndex) => {
                                    const groupColumnIds = group.columns.map(c => c.id);
                                    const allInGroupSelected = groupColumnIds.every(id => selectedColumns.includes(id));

                                    return (
                                        <div key={groupIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                                            <label className="flex items-center p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors border-b border-gray-200">
                                                <input
                                                    type="checkbox"
                                                    checked={allInGroupSelected}
                                                    onChange={() => handleToggleColumnGroup(groupIndex)}
                                                    className="text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="ml-2 text-sm font-semibold text-gray-800">{group.label}</span>
                                                <span className="ml-auto text-xs text-gray-500">
                                                    {groupColumnIds.filter(id => selectedColumns.includes(id)).length}/{groupColumnIds.length}
                                                </span>
                                            </label>
                                            <div className="p-3 space-y-2">
                                                {group.columns.map(col => (
                                                    <label key={col.id} className="flex items-center p-2 cursor-pointer hover:bg-gray-50 rounded text-sm">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedColumns.includes(col.id)}
                                                            onChange={() => handleToggleColumn(col.id)}
                                                            className="text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <span className="ml-2 text-gray-700">{col.title}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Summary */}
                    {!exportComplete && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="text-sm">
                                <span className="font-medium text-blue-900">Ready to export: </span>
                                <span className="text-blue-700">{getExportCount()}</span>
                            </div>
                            {step === 'columns' && (
                                <div className="text-xs text-blue-600 mt-1">
                                    {selectedColumns.length} columns selected
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                    >
                        {exportComplete ? 'Close' : 'Cancel'}
                    </button>

                    {!exportComplete && (
                        <div className="flex gap-3">
                            {step === 'columns' && (
                                <button
                                    onClick={() => setStep('type')}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Back
                                </button>
                            )}

                            {step === 'type' ? (
                                <button
                                    onClick={() => setStep('columns')}
                                    className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button
                                    onClick={handleExport}
                                    disabled={isExporting || selectedColumns.length === 0}
                                    className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                >
                                    {isExporting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            Export CSV
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExportModal;
